import * as React from 'react';
import { createMasonryInstance, driver } from '@leagueplatform/masonry';
import {
  MasonryEngineEvent,
  DEFAULT_NAMESPACE,
} from '@leagueplatform/masonry-engine';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import {
  PageContainer,
  PageNotFound,
} from '@leagueplatform/web-common-components';
import { UnavailableDeviceConnectModal } from '@leagueplatform/health-programs';
import { useDashboardConfig } from '#hooks/use-dashboard-config.ts';
import { AddEditDataManagedModal } from '#components/add-edit-data/add-edit-data-managed-modal-2.tsx';
import { DASHBOARD_MASONRY_HANDLERS } from '../../node-handlers';

type Params = {
  dataType: string;
};

export const Details = () => {
  const { dataType } = useParams<Params>();
  const { metricDetailsMasonryConfigKey } = useDashboardConfig();
  const [addDataModalOpen, setAddDataModalOpen] = React.useState(false);
  const [connectDeviceModalOpen, setConnectDeviceModalOpen] =
    React.useState(false);

  const { Component: MasonryComponent, invokeAction } = React.useMemo(
    () => createMasonryInstance(),
    [],
  );

  React.useEffect(() => {
    const onAddDataClick = () => setAddDataModalOpen(true);
    const onConnectDeviceClick = () => setConnectDeviceModalOpen(true);

    const unsubscribeEmptyStateCta = driver.actionController.addEventListener(
      'deeplink',
      DASHBOARD_MASONRY_HANDLERS.DASHBOARD_METRIC_DETAILS_EMPTY_STATE_PRIMARY_CTA,
      MasonryEngineEvent.ALL,
      onAddDataClick,
    );

    const unsubscribeAddDataCta = driver.actionController.addEventListener(
      'deeplink',
      DASHBOARD_MASONRY_HANDLERS.DASHBOARD_METRIC_DETAILS_ADD_DATA_CTA,
      MasonryEngineEvent.ALL,
      onAddDataClick,
    );

    const unsubscribeConnectDeviceCta =
      driver.actionController.addEventListener(
        'deeplink',
        DASHBOARD_MASONRY_HANDLERS.DASHBOARD_METRIC_DETAILS_EMPTY_STATE_SECONDARY_CTA,
        MasonryEngineEvent.ALL,
        onConnectDeviceClick,
      );

    return () => {
      unsubscribeEmptyStateCta();
      unsubscribeAddDataCta();
      unsubscribeConnectDeviceCta();
    };
  }, []);

  if (!metricDetailsMasonryConfigKey) {
    return <PageNotFound />;
  }

  return (
    <PageContainer>
      <MasonryComponent
        rootNodeId={metricDetailsMasonryConfigKey}
        initialQueryParams={{ metricType: dataType }}
      />
      <AddEditDataManagedModal
        metricType={addDataModalOpen ? dataType : undefined}
        onClose={() => setAddDataModalOpen(false)}
        onSuccessSubmit={() => {
          invokeAction({
            type: 'reload',
            namespace: DEFAULT_NAMESPACE,
            payload: {
              nodeIds: [metricDetailsMasonryConfigKey],
            },
          });
        }}
      />
      {connectDeviceModalOpen && (
        <UnavailableDeviceConnectModal
          onClose={() => setConnectDeviceModalOpen(false)}
        />
      )}
    </PageContainer>
  );
};
